import {
    ServiceConfigoption,
    ServiceSettingProps,
} from '@/modules/Service/types'
import React, { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { GridHookForm, KeyValueDynamic } from 'vnetwork-ui'
import ServiceStatementComponent from '../../../shared/ServiceStatement'
import { useForm } from 'react-hook-form'
import {
    getServiceConfigoptions,
    updateServiceConfigoptions,
} from '@/modules/Service/services'
import { FormItemProps } from 'vnetwork-ui/dist/components/FormItem'
import { Contract } from '@/modules/Contract/types'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

interface UpdateVCloudStatementConfigForm {
    service_configoptions: ServiceConfigoption[]
}

const Statement: React.FC<ServiceSettingProps> = ({ service }) => {
    const { t } = useTranslation()

    const { data, renderState, refetch } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.SERVICES_CONFIGOPTIONS, service?.service_id],
        queryFn: () =>
            getServiceConfigoptions({ serviceID: service?.service_id }),
    })

    const { mutateAsync, isPending: updateServiceConfigoptionLoading } =
        useMutationWithHandler(
            { mutationFn: updateServiceConfigoptions },
            {
                successMessage: t('success'),
                reactQueryKeys: QUERY_KEYS.SERVICES,
                onSuccess: () => {
                    refetch()
                },
            }
        )

    const formControlOptions = useForm<UpdateVCloudStatementConfigForm>({
        defaultValues: {
            service_configoptions: [],
        },
    })

    useEffect(() => {
        formControlOptions.reset({
            service_configoptions: data,
        })
    }, [data])

    const columnStatements = useMemo(
        () =>
            [
                {
                    name: 'name' as any,
                    type: 'textfield',
                    placeholder: t('select-config-vm'),
                    label: t('select-config'),
                    disabled: true,
                },
                {
                    name: 'commitment_price_per_unit' as any,
                    type: 'number',
                    placeholder: t('commitment_price_per_unit'),
                    label: t('commitment_price_per_unit'),
                },
            ] as FormItemProps<Contract>[],
        []
    )

    const filterRenderFunctionTrue = useCallback((items: any[]) => {
        return items?.filter(
            // @ts-ignore
            (item) => item?.statementable
        )
    }, [])

    const handleUpdate = async (values: UpdateVCloudStatementConfigForm) => {
        await mutateAsync({
            serviceID: service?.service_id,
            service_configoptions: values?.service_configoptions,
        })
    }

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    return (
        <>
            <ServiceStatementComponent serviceID={service?.service_id} />
            <div className="bg-white rounded-lg mt-2 px-4 py-2">
                <GridHookForm
                    actions={[
                        {
                            title: t('save'),
                            type: 'submit',
                            variant: 'contained',
                            disabled:
                                !formControlOptions.formState.isDirty ||
                                updateServiceConfigoptionLoading,
                            loading: updateServiceConfigoptionLoading,
                        },
                    ]}
                    data={[
                        {
                            renderEditComponent: (control) => (
                                <KeyValueDynamic
                                    control={control as any}
                                    label={t('service-statement')}
                                    name={`service_configoptions` as any}
                                    columns={columnStatements}
                                    filterRenderFunction={
                                        filterRenderFunctionTrue
                                    }
                                    disabledAddNew
                                    disabledDelete
                                />
                            ),
                            className: 'col-span-4',
                        },
                    ]}
                    formControl={formControlOptions}
                    onSubmit={handleUpdate}
                />
            </div>
        </>
    )
}

export default Statement
